import { useEffect, useMemo, useState } from 'react';
import { Search, Edit, X } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { listDrivers, getDriverDetails, createDriver, updateDriver } from '../../../entities/driver';
import { listVehicles, getVehicleDetails, createVehicle, updateVehicle } from '../../../entities/vehicle';

// Keep these in sync with your backend model (or import if you expose them)
const VEHICLE_TYPES = ['semi-truck', 'box-truck', 'flatbed', 'refrigerated', 'tanker', 'pickup', 'trailer', 'other'];

export function DriversVehiclesPage() {
  const [activeTab, setActiveTab] = useState('drivers');

  // -------------------- DRIVERS STATE (unchanged) --------------------
  const [drivers, setDrivers] = useState([]);
  const [loadingDrivers, setLoadingDrivers] = useState(false);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all'); // all | active | inactive
  const [sortBy] = useState('created_at');
  const [sortDir] = useState('desc');

  // Driver modal + form
  const [isDriverModalOpen, setIsDriverModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [savingDriver, setSavingDriver] = useState(false);

  const emptyDriver = {
    name: '',
    driver_id: '',
    contact: '',
    email: '',
    cdl_number: '',
    cdl_expiry: '', // yyyy-mm-dd
    status: 'active',
  };
  const [driverForm, setDriverForm] = useState(emptyDriver);

  // -------------------- VEHICLES STATE (rewritten to match your model) --------------------
  const [vehicles, setVehicles] = useState([]);
  const [loadingVehicles, setLoadingVehicles] = useState(false);
  const [vPage, setVPage] = useState(1);
  const [vLimit, setVLimit] = useState(10);
  const [vTotal, setVTotal] = useState(0);
  const [vTotalPages, setVTotalPages] = useState(1);

  const [vSearch, setVSearch] = useState('');
  const [vStatusFilter, setVStatusFilter] = useState('all'); // all | active | maintenance | inactive
  const [vSortBy] = useState('created_at');
  const [vSortDir] = useState('desc');

  // Vehicle modal + form
  const [isVehicleModalOpen, setIsVehicleModalOpen] = useState(false);
  const [vEditingId, setVEditingId] = useState(null);
  const [savingVehicle, setSavingVehicle] = useState(false);

  const emptyVehicle = {
    name: '',
    type: VEHICLE_TYPES[0] || 'other',
    capacity: '',
    license_plate: '',
    vin: '',
    status: 'active',
  };
  const [vehicleForm, setVehicleForm] = useState(emptyVehicle);

  // -------------------- HELPERS --------------------
  // Debounce drivers search
  const [debouncedSearch, setDebouncedSearch] = useState(search);
  useEffect(() => {
    const id = setTimeout(() => setDebouncedSearch(search), 400);
    return () => clearTimeout(id);
  }, [search]);

  // Debounce vehicles search
  const [debouncedVSearch, setDebouncedVSearch] = useState(vSearch);
  useEffect(() => {
    const id = setTimeout(() => setDebouncedVSearch(vSearch), 400);
    return () => clearTimeout(id);
  }, [vSearch]);

  const driverQueryString = useMemo(() => {
    const params = new URLSearchParams();
    params.set('page', String(page));
    params.set('limit', String(limit));
    if (debouncedSearch?.trim()) params.set('q', debouncedSearch.trim());
    if (statusFilter !== 'all') params.set('status', statusFilter);
    params.set('sortBy', sortBy);
    params.set('sortDir', sortDir);
    return `?${params.toString()}`;
  }, [page, limit, debouncedSearch, statusFilter, sortBy, sortDir]);

  const vehicleQueryString = useMemo(() => {
    const params = new URLSearchParams();
    params.set('page', String(vPage));
    params.set('limit', String(vLimit));
    if (debouncedVSearch?.trim()) params.set('q', debouncedVSearch.trim());
    if (vStatusFilter !== 'all') params.set('status', vStatusFilter);
    params.set('sortBy', vSortBy);
    params.set('sortDir', vSortDir);
    return `?${params.toString()}`;
  }, [vPage, vLimit, debouncedVSearch, vStatusFilter, vSortBy, vSortDir]);

  // -------------------- LOAD DRIVERS --------------------
  const loadDrivers = async () => {
    setLoadingDrivers(true);
    try {
      const data = await listDrivers(driverQueryString);
      setDrivers(Array.isArray(data.data) ? data.data : []);
      setTotal(Number(data.total || 0));
      setTotalPages(Math.ceil((data.total || 0) / limit));
      if (data.page && data.page !== page) setPage(data.page);
    } catch (err) {
      console.error('Failed to load drivers:', err);
      toast.error('Failed to load drivers');
    } finally {
      setLoadingDrivers(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'drivers') loadDrivers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, driverQueryString]);

  // -------------------- LOAD VEHICLES --------------------
  const loadVehicles = async () => {
    setLoadingVehicles(true);
    try {
      const data = await listVehicles(vehicleQueryString);
      setVehicles(Array.isArray(data.data) ? data.data : []);
      setVTotal(Number(data.total || 0));
      setVTotalPages(Math.ceil((data.total || 0) / vLimit));
      if (data.page && data.page !== vPage) setVPage(data.page);
    } catch (err) {
      console.error('Failed to load vehicles:', err);
      toast.error('Failed to load vehicles');
    } finally {
      setLoadingVehicles(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'vehicles') loadVehicles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, vehicleQueryString]);

  // -------------------- DRIVER FORM HANDLERS (unchanged) --------------------
  const openAddDriver = () => {
    setEditingId(null);
    setDriverForm(emptyDriver);
    setIsDriverModalOpen(true);
  };

  const openEditDriver = async (id) => {
    setEditingId(id);
    setIsDriverModalOpen(true);
    try {
      const d = await getDriverDetails(id);
      setDriverForm({
        name: d.name || '',
        driver_id: d.driver_id || '',
        contact: d.contact || '',
        email: d.email || '',
        cdl_number: d.cdl_number || '',
        cdl_expiry: d.cdl_expiry ? d.cdl_expiry.substring(0, 10) : '',
        status: (d.status || 'active').toLowerCase(),
      });
    } catch (err) {
      console.error('Failed to fetch driver details:', err);
      toast.error('Failed to load driver');
      setIsDriverModalOpen(false);
    }
  };

  const onDriverChange = (e) => {
    const { name, value } = e.target;
    setDriverForm((prev) => ({ ...prev, [name]: value }));
  };

  const submitDriver = async (e) => {
    e.preventDefault();
    if (!driverForm.name.trim() || !driverForm.driver_id.trim()) {
      toast.error('Driver Name and Driver ID are required');
      return;
    }
    setSavingDriver(true);
    try {
      if (editingId) {
        await updateDriver(editingId, { ...driverForm, status: driverForm.status.toLowerCase() });
        toast.success('Driver updated');
      } else {
        await createDriver({ ...driverForm, status: driverForm.status.toLowerCase() });
        toast.success('Driver created');
      }
      setIsDriverModalOpen(false);
      setDriverForm(emptyDriver);
      setEditingId(null);
      loadDrivers();
    } catch (err) {
      console.error('Save driver failed:', err);
      const apiMsg = err?.response?.data?.error || err?.response?.data?.message || 'Failed to save driver';
      toast.error(apiMsg);
    } finally {
      setSavingDriver(false);
    }
  };

  // -------------------- VEHICLE FORM HANDLERS (rewritten for your model) --------------------
  const openAddVehicle = () => {
    setVEditingId(null);
    setVehicleForm(emptyVehicle);
    setIsVehicleModalOpen(true);
  };

  const openEditVehicle = async (id) => {
    setVEditingId(id);
    setIsVehicleModalOpen(true);
    try {
      const v = await getVehicleDetails(id);
      setVehicleForm({
        name: v.name || '',
        type: v.type || VEHICLE_TYPES[0] || 'other',
        capacity: v.capacity || '',
        license_plate: v.license_plate || '',
        vin: v.vin || '',
        status: (v.status || 'active').toLowerCase(),
      });
    } catch (err) {
      console.error('Failed to fetch vehicle details:', err);
      toast.error('Failed to load vehicle');
      setIsVehicleModalOpen(false);
    }
  };

  const onVehicleChange = (e) => {
    const { name, value } = e.target;
    setVehicleForm((prev) => ({ ...prev, [name]: value }));
  };

  const submitVehicle = async (e) => {
    e.preventDefault();
    console.log(vehicleForm);
    // Required: name and type (per your model)
    if (!vehicleForm.name.trim() || !vehicleForm.type.trim()) {
      toast.error('Vehicle Name and Type are required');
      return;
    }
    setSavingVehicle(true);
    try {
      const payload = {
        ...vehicleForm,
        status: vehicleForm.status.toLowerCase(),
      };
      if (vEditingId) {
        await updateVehicle(vEditingId, payload);
        toast.success('Vehicle updated');
      } else {
        await createVehicle(payload);
        toast.success('Vehicle created');
      }
      setIsVehicleModalOpen(false);
      setVehicleForm(emptyVehicle);
      setVEditingId(null);
      loadVehicles();
    } catch (err) {
      console.error('Save vehicle failed:', err);
      const apiMsg = err?.response?.data?.error || err?.response?.data?.message || 'Failed to save vehicle';
      toast.error(apiMsg);
    } finally {
      setSavingVehicle(false);
    }
  };

  // -------------------- UI HELPERS --------------------
  const statusPill = (status) => {
    const map = {
      active: 'bg-green-100 text-green-700',
      maintenance: 'bg-yellow-100 text-yellow-700',
      inactive: 'bg-gray-200 text-gray-700',
    };
    return <span className={`px-2 py-1 text-xs rounded-full ${map[status] || ''}`}>{status?.charAt(0)?.toUpperCase() + status?.slice(1)}</span>;
  };

  // -------------------- RENDER --------------------
  return (
    <div className="p-6 w-full space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h2 className="text-lg font-semibold">Drivers & Vehicles Management</h2>
        <div className="flex gap-2 bg-gray-100 rounded-lg p-1">
          <button onClick={() => setActiveTab('drivers')} className={`px-4 py-1 rounded-md text-sm ${activeTab === 'drivers' ? 'bg-white shadow text-gray-800' : 'text-gray-500'}`}>
            Driver
          </button>
          <button onClick={() => setActiveTab('vehicles')} className={`px-4 py-1 rounded-md text-sm ${activeTab === 'vehicles' ? 'bg-white shadow text-gray-800' : 'text-gray-500'}`}>
            Vehicle
          </button>
        </div>
      </div>

      {/* Drivers controls */}
      {activeTab === 'drivers' && (
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 flex-1">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search drivers..."
                value={search}
                onChange={(e) => {
                  setPage(1);
                  setSearch(e.target.value);
                }}
                className="pl-9 pr-3 py-2 rounded-lg border border-gray-300 bg-white text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 w-full"
              />
            </div>
            <select
              value={statusFilter}
              onChange={(e) => {
                setPage(1);
                setStatusFilter(e.target.value);
              }}
              className="px-3 py-2 rounded-lg border border-gray-300 bg-white text-sm"
            >
              <option value="all">All Drivers</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
            <select
              value={limit}
              onChange={(e) => {
                setPage(1);
                setLimit(Number(e.target.value));
              }}
              className="px-3 py-2 rounded-lg border border-gray-300 bg-white text-sm"
            >
              {[10, 20, 50].map((n) => (
                <option key={n} value={n}>
                  {n} / page
                </option>
              ))}
            </select>
          </div>

          <button onClick={openAddDriver} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
            + Add Driver
          </button>
        </div>
      )}

      {/* Drivers table (unchanged) */}
      {activeTab === 'drivers' && (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          {loadingDrivers ? (
            <div className="p-8 text-center text-sm text-gray-500">Loading drivers…</div>
          ) : (
            <>
              <table className="w-full text-sm text-left">
                <thead className="bg-gray-50 text-gray-600 font-medium">
                  <tr>
                    <th className="px-6 py-3">DRIVER</th>
                    <th className="px-6 py-3">CONTACT</th>
                    <th className="px-6 py-3">CDL INFO</th>
                    <th className="px-6 py-3">STATUS</th>
                    <th className="px-6 py-3">ACTIONS</th>
                  </tr>
                </thead>
                <tbody>
                  {drivers.length === 0 && (
                    <tr>
                      <td colSpan={5} className="px-6 py-8 text-center text-gray-500">
                        No drivers found.
                      </td>
                    </tr>
                  )}
                  {drivers.map((d) => (
                    <tr key={d.id} className="border-t">
                      <td className="px-6 py-3 font-semibold text-gray-800">
                        {d.name}
                        <p className="text-xs text-gray-500">ID: {d.driver_id}</p>
                      </td>
                      <td className="px-6 py-3 text-gray-700">
                        <p>{d.contact || '-'}</p>
                        <p className="text-xs">{d.email || '-'}</p>
                      </td>
                      <td className="px-6 py-3 text-gray-700">
                        <p className="font-medium">{d.cdl_number ? d.cdl_number : '-'}</p>
                        <p className="text-xs">Exp: {d.cdl_expiry ? new Date(d.cdl_expiry).toLocaleDateString() : '-'}</p>
                      </td>
                      <td className="px-6 py-3">{statusPill((d.status || 'active').toLowerCase())}</td>
                      <td className="px-6 py-3">
                        <button onClick={() => openEditDriver(d.id)} className="inline-flex items-center gap-1 text-gray-600 hover:text-blue-600" title="Edit">
                          <Edit className="w-4 h-4" />
                          Edit
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <div className="flex items-center justify-between px-6 py-3 bg-gray-50 border-t">
                <span className="text-sm text-gray-500">
                  Showing{' '}
                  <span className="font-medium">
                    {drivers.length === 0 ? 0 : (page - 1) * limit + 1} - {Math.min(page * limit, total)}
                  </span>{' '}
                  of {total}
                </span>
                <div className="flex items-center gap-1">
                  <button disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))} className="px-2 py-1 text-sm rounded hover:bg-gray-200 disabled:opacity-50">
                    &lt;
                  </button>
                  <span className="px-3 py-1 text-sm rounded bg-blue-600 text-white">{page}</span>
                  <button disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))} className="px-2 py-1 text-sm rounded hover:bg-gray-200 disabled:opacity-50">
                    &gt;
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {/* Vehicles tab (rewritten) */}
      {activeTab === 'vehicles' && (
        <>
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3 flex-1">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search vehicles..."
                  value={vSearch}
                  onChange={(e) => {
                    setVPage(1);
                    setVSearch(e.target.value);
                  }}
                  className="pl-9 pr-3 py-2 rounded-lg border border-gray-300 bg-white text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 w-full"
                />
              </div>

              <select
                value={vStatusFilter}
                onChange={(e) => {
                  setVPage(1);
                  setVStatusFilter(e.target.value);
                }}
                className="px-3 py-2 rounded-lg border border-gray-300 bg-white text-sm"
              >
                <option value="all">All Vehicles</option>
                <option value="active">Active</option>
                <option value="maintenance">Maintenance</option>
                <option value="inactive">Inactive</option>
              </select>

              <select
                value={vLimit}
                onChange={(e) => {
                  setVPage(1);
                  setVLimit(Number(e.target.value));
                }}
                className="px-3 py-2 rounded-lg border border-gray-300 bg-white text-sm"
              >
                {[10, 20, 50].map((n) => (
                  <option key={n} value={n}>
                    {n} / page
                  </option>
                ))}
              </select>
            </div>

            <button onClick={openAddVehicle} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
              + Add Vehicle
            </button>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
            {loadingVehicles ? (
              <div className="p-8 text-center text-sm text-gray-500">Loading vehicles…</div>
            ) : (
              <>
                <table className="w-full text-sm text-left">
                  <thead className="bg-gray-50 text-gray-600 font-medium">
                    <tr>
                      <th className="px-6 py-3">VEHICLE</th>
                      <th className="px-6 py-3">TYPE / CAPACITY</th>
                      <th className="px-6 py-3">LICENSE / VIN</th>
                      <th className="px-6 py-3">STATUS</th>
                      <th className="px-6 py-3">ACTIONS</th>
                    </tr>
                  </thead>
                  <tbody>
                    {vehicles.length === 0 && (
                      <tr>
                        <td colSpan={5} className="px-6 py-8 text-center text-gray-500">
                          No vehicles found.
                        </td>
                      </tr>
                    )}
                    {vehicles.map((v) => (
                      <tr key={v.id} className="border-t">
                        <td className="px-6 py-3 font-semibold text-gray-800">
                          {v.name}
                          <p className="text-xs text-gray-500">Type: {v.type || '-'}</p>
                        </td>
                        <td className="px-6 py-3 text-gray-700">
                          <p className="font-medium">{v.capacity || '-'}</p>
                        </td>
                        <td className="px-6 py-3 text-gray-700">
                          <p className="font-medium">{v.license_plate || '-'}</p>
                          <p className="text-xs">{v.vin || '-'}</p>
                        </td>
                        <td className="px-6 py-3">{statusPill((v.status || 'active').toLowerCase())}</td>
                        <td className="px-6 py-3">
                          <button onClick={() => openEditVehicle(v.id)} className="inline-flex items-center gap-1 text-gray-600 hover:text-blue-600" title="Edit">
                            <Edit className="w-4 h-4" />
                            Edit
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                <div className="flex items-center justify-between px-6 py-3 bg-gray-50 border-t">
                  <span className="text-sm text-gray-500">
                    Showing{' '}
                    <span className="font-medium">
                      {vehicles.length === 0 ? 0 : (vPage - 1) * vLimit + 1} - {Math.min(vPage * vLimit, vTotal)}
                    </span>{' '}
                    of {vTotal}
                  </span>
                  <div className="flex items-center gap-1">
                    <button disabled={vPage <= 1} onClick={() => setVPage((p) => Math.max(1, p - 1))} className="px-2 py-1 text-sm rounded hover:bg-gray-200 disabled:opacity-50">
                      &lt;
                    </button>
                    <span className="px-3 py-1 text-sm rounded bg-blue-600 text-white">{vPage}</span>
                    <button disabled={vPage >= vTotalPages} onClick={() => setVPage((p) => Math.min(vTotalPages, p + 1))} className="px-2 py-1 text-sm rounded hover:bg-gray-200 disabled:opacity-50">
                      &gt;
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </>
      )}

      {/* Add / Edit Driver Modal (unchanged) */}
      {isDriverModalOpen && (
        <Modal
          title={editingId ? 'Edit Driver' : 'Add Driver'}
          onClose={() => {
            if (!savingDriver) {
              setIsDriverModalOpen(false);
              setEditingId(null);
              setDriverForm(emptyDriver);
            }
          }}
        >
          <form onSubmit={submitDriver} className="space-y-4">
            <FormField label="Driver Name" name="name" value={driverForm.name} onChange={onDriverChange} required />
            <FormField label="Driver ID" name="driver_id" value={driverForm.driver_id} onChange={onDriverChange} required />
            <FormField label="Contact" name="contact" value={driverForm.contact} onChange={onDriverChange} />
            <FormField label="Email" name="email" value={driverForm.email} onChange={onDriverChange} type="email" />
            <FormField label="CDL Number" name="cdl_number" value={driverForm.cdl_number} onChange={onDriverChange} />
            <FormField label="CDL Expiry" name="cdl_expiry" value={driverForm.cdl_expiry} onChange={onDriverChange} type="date" />
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select name="status" value={driverForm.status} onChange={onDriverChange} className="w-full px-3 py-2 rounded-lg border border-gray-300 bg-white text-sm">
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <button
                type="button"
                onClick={() => {
                  if (!savingDriver) {
                    setIsDriverModalOpen(false);
                    setEditingId(null);
                    setDriverForm(emptyDriver);
                  }
                }}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-100"
              >
                Cancel
              </button>
              <button type="submit" disabled={savingDriver} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50">
                {savingDriver ? 'Saving...' : editingId ? 'Save Changes' : 'Save Driver'}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {/* Add / Edit Vehicle Modal (rewritten to match model) */}
      {isVehicleModalOpen && (
        <Modal
          title={vEditingId ? 'Edit Vehicle' : 'Add Vehicle'}
          onClose={() => {
            if (!savingVehicle) {
              setIsVehicleModalOpen(false);
              setVEditingId(null);
              setVehicleForm(emptyVehicle);
            }
          }}
        >
          <form onSubmit={submitVehicle} className="space-y-4">
            <FormField label="Vehicle Name" name="name" value={vehicleForm.name} onChange={onVehicleChange} required />

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Type <span className="text-xs text-gray-500 ml-2">({VEHICLE_TYPES.join(', ')})</span>
              </label>
              <select name="type" value={vehicleForm.type} onChange={onVehicleChange} className="w-full px-3 py-2 rounded-lg border border-gray-300 bg-white text-sm capitalize" required>
                {VEHICLE_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </div>

            <FormField label="Capacity" name="capacity" value={vehicleForm.capacity} onChange={onVehicleChange} />
            <FormField label="License Plate" name="license_plate" value={vehicleForm.license_plate} onChange={onVehicleChange} />
            <FormField label="VIN" name="vin" value={vehicleForm.vin} onChange={onVehicleChange} />
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select name="status" value={vehicleForm.status} onChange={onVehicleChange} className="w-full px-3 py-2 rounded-lg border border-gray-300 bg-white text-sm">
                <option value="active">Active</option>
                <option value="maintenance">Maintenance</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <button
                type="button"
                onClick={() => {
                  if (!savingVehicle) {
                    setIsVehicleModalOpen(false);
                    setVEditingId(null);
                    setVehicleForm(emptyVehicle);
                  }
                }}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-100"
              >
                Cancel
              </button>
              <button type="submit" disabled={savingVehicle} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50">
                {savingVehicle ? 'Saving...' : vEditingId ? 'Save Changes' : 'Save Vehicle'}
              </button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}

/* -------------------- UI PRIMITIVES (kept same) -------------------- */
function Modal({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-lg w-full max-w-lg p-6 relative">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold text-gray-800">{title}</h2>
          <button onClick={onClose}>
            <X className="w-5 h-5 text-gray-500 hover:text-gray-700" />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

function FormField({ label, name, value, onChange, type = 'text', required = false }) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <input type={type} name={name} value={value} onChange={onChange} required={required} className="w-full px-3 py-2 rounded-lg border border-gray-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
    </div>
  );
}
